import React, { useState, useContext } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, FormControlLabel, RadioGroup, Radio,
  Select, MenuItem, Button, Box
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';

import { useCreateTimeRule } from '@mutation/timeRuleMutation';
import { AlertContext } from "@context/AlertProvider";
import { dayNumberToDayName, monthNumberToMonthName } from "utils/timeUtil";
import { formatUtcTimeFromLocal} from "@utils/timeUtil"

const months = Array.from({ length: 12 }, (_, i) => i);
const daysOfWeek = Array.from({ length: 7 }, (_, i) => i);

function CreateTimeRuleModal({ open, onClose, clubUuid, siteUuid }) {
  const [monthMode, setMonthMode] = useState('any');
  const [selectedMonths, setSelectedMonths] = useState([]);

  const [dayMode, setDayMode] = useState('any');
  const [selectedDays, setSelectedDays] = useState([]);

  const [timeMode, setTimeMode] = useState('full');
  const [startTime, setStartTime] = useState(null); // dayjs object
  const [endTime, setEndTime] = useState(null); // dayjs object

  const createTimeRule = useCreateTimeRule();
  const { addAlertBanner } = useContext(AlertContext);

  const resetState = () => {
    setMonthMode('any');
    setSelectedMonths([]);
    setDayMode('any');
    setSelectedDays([]);
    setTimeMode('full');
    setStartTime(null);
    setEndTime(null);
  };

  const handleSubmit = () => {
    if (timeMode === 'range') {
      if (!startTime || !endTime) {
        addAlertBanner("Prosím zadejte čas od a do.", "error");
        return;
      }
      if (startTime.isAfter(endTime) || startTime.isSame(endTime)) {
        addAlertBanner("Čas od musí být před časem do.", "error");
        return;
      }
    }

    const payload = {
      club_uuid: clubUuid,
      site_uuid: siteUuid,
      months: monthMode === 'any' ? [] : selectedMonths,
      days_of_week: dayMode === 'any' ? [] : selectedDays,
      day_of_month: 0,
      start_time:
        timeMode === 'full'
          ? "00:00:00.000Z"
          : formatUtcTimeFromLocal(startTime.$H, startTime.$m),
      end_time:
        timeMode === 'full'
          ? "23:59:59.000Z"
          : formatUtcTimeFromLocal(endTime.$H, endTime.$m),
    };

    createTimeRule.mutate({ data: payload }, {
      onSuccess: () => {
        addAlertBanner("Interval úspěšně vytvořen.", "success");
        resetState();
        onClose();
      },
      onError: (err) => {
        if (err.status === 403) {
          addAlertBanner("Nemáte oprávnění k vytvoření intervalu.", "error");
        } else {
          addAlertBanner("Nepodařilo se vytvořit interval.", "error");
        }
      }
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Nevytížený interval</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

        {/* Měsíce */}
        <FormControl>
          <RadioGroup row value={monthMode} onChange={(e) => setMonthMode(e.target.value)}>
            <FormControlLabel value="any" control={<Radio />} label="Kterýkoliv měsíc" />
            <FormControlLabel value="select" control={<Radio />} label="Vybrat Měsíce" />
          </RadioGroup>
          {monthMode === 'select' && (
            <Select
              multiple
              value={selectedMonths}
              onChange={(e) => setSelectedMonths(e.target.value)}
              displayEmpty
              fullWidth
            >
              {months.map((m) => (
                <MenuItem key={m} value={m}>{monthNumberToMonthName(m)}</MenuItem>
              ))}
            </Select>
          )}
        </FormControl>

        {/* Dny v týdnu */}
        <FormControl>
          <RadioGroup row value={dayMode} onChange={(e) => setDayMode(e.target.value)}>
            <FormControlLabel value="any" control={<Radio />} label="Kterýkoliv den v týdnu" />
            <FormControlLabel value="select" control={<Radio />} label="Vybrat Dny v týdnu" />
          </RadioGroup>
          {dayMode === 'select' && (
            <Select
              multiple
              value={selectedDays}
              onChange={(e) => setSelectedDays(e.target.value)}
              displayEmpty
              fullWidth
            >
              {daysOfWeek.map((d) => (
                <MenuItem key={d} value={d}>{dayNumberToDayName(d)}</MenuItem>
              ))}
            </Select>
          )}
        </FormControl>

        {/* Čas v dni */}
        <FormControl>
          <RadioGroup row value={timeMode} onChange={(e) => setTimeMode(e.target.value)}>
            <FormControlLabel value="full" control={<Radio />} label="Celý den" />
            <FormControlLabel value="range" control={<Radio />} label="Vybrat čas" />
          </RadioGroup>
          {timeMode === 'range' && (
            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
              <TimePicker
                label="Od"
                ampm={false}
                value={startTime}
                onChange={(newValue) => setStartTime(newValue)}
                format="HH:mm"
                minutesStep={10}
              />
              <TimePicker
                label="Do"
                ampm={false}
                value={endTime}
                onChange={(newValue) => setEndTime(newValue)}
                format="HH:mm"
                minutesStep={10}
              />
            </Box>
          )}
        </FormControl>

      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" variant="outlined">Cancel</Button>
        <Button onClick={handleSubmit} color="primary" variant="contained">Save</Button>
      </DialogActions>
    </Dialog>
  );
}

export default CreateTimeRuleModal;
